import React, { useState } from 'react';
import {
  Box,
  Container,
  Typography,
  Grid,
  Card,
  CardContent,
  TextField,
  Button,
  MenuItem,
  Step,
  Stepper,
  StepLabel,
  Alert,
  Paper,
  Radio,
  RadioGroup,
  FormControlLabel,
  FormControl,
  FormLabel,
} from '@mui/material';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { TimePicker } from '@mui/x-date-pickers/TimePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { fr } from 'date-fns/locale';
import { Formik, Form } from 'formik';
import * as Yup from 'yup';
import {
  EventAvailable,
  LocationOn,
  AccessTime,
  Description,
} from '@mui/icons-material';

const steps = ['Type de Service', 'Informations Personnelles', 'Date et Heure', 'Confirmation'];

const serviceTypes = [
  { value: 'maritime', label: 'Groupage Maritime' },
  { value: 'air', label: 'Groupage Aérien' },
  { value: 'moving', label: 'Déménagement International' },
  { value: 'shopping', label: 'Achat International' },
];

const locations = [
  { value: 'paris', label: 'La Courneuve, Paris' },
  { value: 'abidjan-angre', label: 'Angré, Abidjan' },
  { value: 'abidjan-cocody', label: 'Cocody, Abidjan' },
];

const validationSchema = Yup.object().shape({
  serviceType: Yup.string().required('Veuillez sélectionner un service'),
  firstName: Yup.string().required('Le prénom est requis'),
  lastName: Yup.string().required('Le nom est requis'),
  email: Yup.string().email('Email invalide').required('L\'email est requis'),
  phone: Yup.string().required('Le numéro de téléphone est requis'),
  location: Yup.string().required('Veuillez sélectionner une agence'),
  date: Yup.date().required('La date est requise'),
  time: Yup.date().required('L\'heure est requise'),
  description: Yup.string(),
  preferredContact: Yup.string().required('Veuillez sélectionner un mode de contact préféré'),
});

const initialValues = {
  serviceType: '',
  firstName: '',
  lastName: '',
  email: '',
  phone: '',
  location: '',
  date: null,
  time: null,
  description: '',
  preferredContact: 'email',
};

const isStepValid = (step, values, errors) => {
  switch (step) {
    case 0:
      return values.serviceType !== '';
    case 1:
      return (
        values.firstName !== '' &&
        values.lastName !== '' &&
        values.email !== '' &&
        values.phone !== '' &&
        !errors.firstName &&
        !errors.lastName &&
        !errors.email &&
        !errors.phone
      );
    case 2:
      return (
        values.location !== '' &&
        values.date !== null &&
        values.time !== null &&
        !errors.location &&
        !errors.date &&
        !errors.time
      );
    default:
      return true;
  }
};

const Appointment = () => {
  const [activeStep, setActiveStep] = useState(0);
  const [submitted, setSubmitted] = useState(false);

  const handleNext = () => {
    setActiveStep((prevStep) => prevStep + 1);
  };

  const handleBack = () => {
    setActiveStep((prevStep) => prevStep - 1);
  };

  const handleSubmit = (values, { setSubmitting }) => {
    // Here you would typically send the data to your backend
    console.log('Form submitted:', values);
    setSubmitting(false);
    setSubmitted(true);
    handleNext();
  };

  const renderStepContent = (step, formikProps) => {
    const { values, errors, touched, handleChange, setFieldValue } = formikProps;

    switch (step) {
      case 0:
        return (
          <Box>
            <FormControl fullWidth>
              <TextField
                select
                label="Type de Service"
                name="serviceType"
                value={values.serviceType}
                onChange={handleChange}
                error={touched.serviceType && Boolean(errors.serviceType)}
                helperText={touched.serviceType && errors.serviceType}
                fullWidth
                margin="normal"
              >
                {serviceTypes.map((option) => (
                  <MenuItem key={option.value} value={option.value}>
                    {option.label}
                  </MenuItem>
                ))}
              </TextField>
            </FormControl>
            <TextField
              multiline
              rows={4}
              label="Description de vos besoins (optionnel)"
              name="description"
              value={values.description}
              onChange={handleChange}
              fullWidth
              margin="normal"
            />
          </Box>
        );

      case 1:
        return (
          <Grid container spacing={2}>
            <Grid item xs={12} sm={6}>
              <TextField
                label="Prénom"
                name="firstName"
                value={values.firstName}
                onChange={handleChange}
                error={touched.firstName && Boolean(errors.firstName)}
                helperText={touched.firstName && errors.firstName}
                fullWidth
                margin="normal"
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                label="Nom"
                name="lastName"
                value={values.lastName}
                onChange={handleChange}
                error={touched.lastName && Boolean(errors.lastName)}
                helperText={touched.lastName && errors.lastName}
                fullWidth
                margin="normal"
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                label="Email"
                name="email"
                type="email"
                value={values.email}
                onChange={handleChange}
                error={touched.email && Boolean(errors.email)}
                helperText={touched.email && errors.email}
                fullWidth
                margin="normal"
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                label="Téléphone"
                name="phone"
                value={values.phone}
                onChange={handleChange}
                error={touched.phone && Boolean(errors.phone)}
                helperText={touched.phone && errors.phone}
                fullWidth
                margin="normal"
              />
            </Grid>
            <Grid item xs={12}>
              <FormControl component="fieldset" margin="normal">
                <FormLabel component="legend">Mode de contact préféré</FormLabel>
                <RadioGroup
                  row
                  name="preferredContact"
                  value={values.preferredContact}
                  onChange={handleChange}
                >
                  <FormControlLabel value="email" control={<Radio />} label="Email" />
                  <FormControlLabel value="phone" control={<Radio />} label="Téléphone" />
                </RadioGroup>
              </FormControl>
            </Grid>
          </Grid>
        );

      case 2:
        return (
          <Grid container spacing={2}>
            <Grid item xs={12}>
              <TextField
                select
                label="Agence"
                name="location"
                value={values.location}
                onChange={handleChange}
                error={touched.location && Boolean(errors.location)}
                helperText={touched.location && errors.location}
                fullWidth
                margin="normal"
              >
                {locations.map((option) => (
                  <MenuItem key={option.value} value={option.value}>
                    {option.label}
                  </MenuItem>
                ))}
              </TextField>
            </Grid>
            <Grid item xs={12} sm={6}>
              <LocalizationProvider dateAdapter={AdapterDateFns} adapterLocale={fr}>
                <DatePicker
                  label="Date du rendez-vous"
                  value={values.date}
                  onChange={(newValue) => {
                    setFieldValue('date', newValue);
                  }}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      fullWidth
                      margin="normal"
                      error={touched.date && Boolean(errors.date)}
                      helperText={touched.date && errors.date}
                    />
                  )}
                />
              </LocalizationProvider>
            </Grid>
            <Grid item xs={12} sm={6}>
              <LocalizationProvider dateAdapter={AdapterDateFns} adapterLocale={fr}>
                <TimePicker
                  label="Heure du rendez-vous"
                  value={values.time}
                  onChange={(newValue) => {
                    setFieldValue('time', newValue);
                  }}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      fullWidth
                      margin="normal"
                      error={touched.time && Boolean(errors.time)}
                      helperText={touched.time && errors.time}
                    />
                  )}
                />
              </LocalizationProvider>
            </Grid>
          </Grid>
        );

      case 3:
        if (submitted) {
          return (
            <Alert severity="success" sx={{ mt: 2 }}>
              Votre rendez-vous a été programmé avec succès ! Vous recevrez bientôt un email de confirmation.
            </Alert>
          );
        }
        return (
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Résumé de votre rendez-vous
              </Typography>
              <Grid container spacing={2}>
                <Grid item xs={12}>
                  <Typography variant="body1">
                    <strong>Service :</strong> {serviceTypes.find(s => s.value === values.serviceType)?.label}
                  </Typography>
                </Grid>
                <Grid item xs={12}>
                  <Typography variant="body1">
                    <strong>Nom complet :</strong> {values.firstName} {values.lastName}
                  </Typography>
                </Grid>
                <Grid item xs={12}>
                  <Typography variant="body1">
                    <strong>Contact :</strong> {values.email} / {values.phone}
                  </Typography>
                </Grid>
                <Grid item xs={12}>
                  <Typography variant="body1">
                    <strong>Agence :</strong> {locations.find(l => l.value === values.location)?.label}
                  </Typography>
                </Grid>
                <Grid item xs={12}>
                  <Typography variant="body1">
                    <strong>Date et heure :</strong>{' '}
                    {values.date && new Date(values.date).toLocaleDateString('fr-FR')}{' '}
                    {values.time && new Date(values.time).toLocaleTimeString('fr-FR')}
                  </Typography>
                </Grid>
                {values.description && (
                  <Grid item xs={12}>
                    <Typography variant="body1">
                      <strong>Description :</strong> {values.description}
                    </Typography>
                  </Grid>
                )}
              </Grid>
            </CardContent>
          </Card>
        );

      default:
        return null;
    }
  };

  return (
    <Box>
      {/* Hero Section */}
      <Box
        sx={{
          bgcolor: 'primary.main',
          color: 'white',
          py: 8,
          mb: 6,
        }}
      >
        <Container maxWidth="lg">
          <Typography variant="h2" gutterBottom>
            Prendre Rendez-vous
          </Typography>
          <Typography variant="h5">
            Planifiez une rencontre avec nos experts pour discuter de vos besoins
          </Typography>
        </Container>
      </Box>

      <Container maxWidth="md">
        {/* Features */}
        <Grid container spacing={4} sx={{ mb: 6 }}>
          <Grid item xs={12} sm={6} md={3}>
            <Paper sx={{ p: 2, textAlign: 'center' }}>
              <EventAvailable sx={{ fontSize: 40, color: 'primary.main', mb: 1 }} />
              <Typography variant="h6" gutterBottom>
                Simple
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Réservation rapide en ligne
              </Typography>
            </Paper>
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <Paper sx={{ p: 2, textAlign: 'center' }}>
              <LocationOn sx={{ fontSize: 40, color: 'primary.main', mb: 1 }} />
              <Typography variant="h6" gutterBottom>
                Flexible
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Plusieurs agences disponibles
              </Typography>
            </Paper>
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <Paper sx={{ p: 2, textAlign: 'center' }}>
              <AccessTime sx={{ fontSize: 40, color: 'primary.main', mb: 1 }} />
              <Typography variant="h6" gutterBottom>
                Pratique
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Horaires adaptés
              </Typography>
            </Paper>
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <Paper sx={{ p: 2, textAlign: 'center' }}>
              <Description sx={{ fontSize: 40, color: 'primary.main', mb: 1 }} />
              <Typography variant="h6" gutterBottom>
                Personnalisé
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Service sur mesure
              </Typography>
            </Paper>
          </Grid>
        </Grid>

        {/* Stepper */}
        <Stepper activeStep={activeStep} sx={{ mb: 4 }}>
          {steps.map((label) => (
            <Step key={label}>
              <StepLabel>{label}</StepLabel>
            </Step>
          ))}
        </Stepper>

        {/* Form */}
        <Card sx={{ mb: 4 }}>
          <CardContent>
            <Formik
              initialValues={initialValues}
              validationSchema={validationSchema}
              onSubmit={handleSubmit}
            >
              {(formikProps) => (
                <Form>
                  {renderStepContent(activeStep, formikProps)}
                  <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 3 }}>
                    {activeStep > 0 && (
                      <Button onClick={handleBack} sx={{ mr: 1 }}>
                        Retour
                      </Button>
                    )}
                    {activeStep < steps.length - 1 ? (
                      <Button
                        variant="contained"
                        onClick={handleNext}
                        disabled={!isStepValid(activeStep, formikProps.values, formikProps.errors)}
                      >
                        Suivant
                      </Button>
                    ) : (
                      !submitted && (
                        <Button
                          variant="contained"
                          type="submit"
                          disabled={!formikProps.isValid || formikProps.isSubmitting}
                        >
                          Confirmer le rendez-vous
                        </Button>
                      )
                    )}
                  </Box>
                </Form>
              )}
            </Formik>
          </CardContent>
        </Card>
      </Container>
    </Box>
  );
};

export default Appointment;
